import type { Editor } from '@tiptap/react'
import type { EditorMode, ToolbarItem } from '../../types'
import { MODE_ALLOWED, TOOLBAR_DEFAULTS } from './defaults'

const VALID_NODE_TYPES = new Set(['image', 'mergeTag', 'ctaButton'])

/**
 * True when the doc has no text (after trim) and no embedded "valid" nodes
 * (image / mention / mergeTag / ctaButton). Hard breaks and empty paragraphs
 * count as blank — submitting them would result in nothing being sent.
 */
export function isDocBlank(editor: Editor): boolean {
  const doc = editor.state.doc
  if (doc.textContent.trim() !== '') return false
  let hasValidNodes = false
  doc.descendants(node => {
    if (hasValidNodes) return false
    if (VALID_NODE_TYPES.has(node.type.name)) {
      hasValidNodes = true
      return false
    }
    return true
  })
  return !hasValidNodes
}

/**
 * Resolves the toolbar items to render for a given editor mode, falling back to
 * the mode's defaults when no custom set is provided and filtering out any
 * items that aren't allowed in that mode.
 */
export function resolveItems(
  mode: EditorMode,
  customToolbar?: ToolbarItem[],
): ToolbarItem[] {
  const requested = customToolbar ?? TOOLBAR_DEFAULTS[mode]
  const allowed = MODE_ALLOWED[mode]
  return requested.filter(item => allowed.has(item))
}

/**
 * Inserts `text` at the current selection, padding with spaces on either side
 * so the inserted content doesn't collide with adjacent non-whitespace
 * characters.
 *
 * @param opts.alwaysTrailingSpace - Always ensure a trailing space, even when
 * the cursor is at the end of the block.
 * @param opts.cursorBeforeTrailingSpace - When a trailing space is added, move
 * the cursor back so it sits before that space (useful when the caller wants
 * the user to keep typing inside the just-inserted token).
 */
export function insertWithBoundarySpaces(
  editor: Editor,
  text: string,
  opts: {
    cursorBeforeTrailingSpace?: boolean
    alwaysTrailingSpace?: boolean
  } = {},
) {
  const { $from } = editor.state.selection
  const prev = $from.parent.textBetween(
    Math.max(0, $from.parentOffset - 1),
    $from.parentOffset,
  )
  const after = $from.parent.textBetween(
    $from.parentOffset,
    Math.min($from.parent.content.size, $from.parentOffset + 1),
  )
  const needsLeading = prev.length > 0 && !/\s/.test(prev)
  const needsTrailing = opts.alwaysTrailingSpace
    ? after.length === 0 || !/\s/.test(after)
    : after.length > 0 && !/\s/.test(after)
  const content = (needsLeading ? ' ' : '') + text + (needsTrailing ? ' ' : '')

  editor.chain().focus().insertContent(content).run()

  if (needsTrailing && opts.cursorBeforeTrailingSpace) {
    const pos = editor.state.selection.from - 1
    editor.chain().setTextSelection(pos).run()
  }
}
